/**
 * The partner's form as the workbench pre-filled it, read-only: the case's
 * own (which never leaves Hub), or one assignment's copy — what was put on
 * the partner's Backoffice request at Start, or refused there (with why).
 * Delivery is a person's click in Setup → Partner work; nothing here
 * reaches a partner.
 */

import { useEffect, useState } from 'react';

import { Badge, Center, Group, Loader, Stack, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { DocxContent } from '@/components/Containers/DocxContent';
import { vnextApiClient } from '@/services/vnext-api';
import { prefillCopies, type WorkbenchHeader, type WorkbenchPrefill } from '@/types/workbench';
import { toError } from '@/utils/errors';

/** The copy's state, in words. */
export function prefillState(prefill: WorkbenchPrefill): string {
  if (prefill.scope === 'case') {
    return 'The case’s own — never sent; each partner gets their copy at Start';
  }
  if (prefill.status === 'delivered') {
    return `On the partner’s request${prefill.npRequestId !== null ? ` (${prefill.npRequestId})` : ''}`;
  }
  if (prefill.deliveryError) {
    return `Not on the partner’s request — ${prefill.deliveryError}; Deliver it from Setup → Partner work`;
  }
  return 'Not on the partner’s request yet — Deliver it from Setup → Partner work';
}

interface PrefillViewProps {
  header: WorkbenchHeader;
  prefill: WorkbenchPrefill;
}

export function PrefillView({ header, prefill }: Readonly<PrefillViewProps>) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(null);
    vnextApiClient
      .getWorkbenchPrefillFile(header.reportId, prefill.id)
      .then((file) => {
        if (!cancelled) {
          setUrl(file.url);
        }
      })
      .catch((err: unknown) => {
        Sentry.captureException(toError(err));
        if (!cancelled) {
          setFailed(toError(err, "The form couldn't be opened").message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [header.reportId, prefill.id]);

  const copies = prefillCopies(prefill);
  return (
    <Stack gap="sm" data-testid="prefill-view">
      <Group gap={6}>
        <Badge size="sm" variant="light" color="grape">
          Partner&apos;s form
        </Badge>
        <Text size="xs" c="dimmed">
          Read-only · {prefill.filename}
          {copies > 0 ? ` · ${copies} ${copies === 1 ? 'table' : 'tables'} pre-filled` : ''}
          {prefill.status === 'superseded' ? ' · superseded by a newer build' : ''}
        </Text>
      </Group>
      <Text size="xs" c="dimmed" data-testid="prefill-state">
        {prefillState(prefill)}
      </Text>
      {failed ? (
        <Text size="sm" c="red">
          {failed}
        </Text>
      ) : !url ? (
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      ) : (
        <DocxContent key={prefill.id} docxUrl={url} cacheKey={`prefill:${prefill.id}`} />
      )}
    </Stack>
  );
}
